import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

/**
 * Connect a project's website so approved audit fixes can be applied to it.
 *
 * Two routes in, because the people using this are marketers rather than
 * sysadmins:
 *
 *   Pairing (default) — install the plugin, paste a code. Nobody has to find
 *   WordPress's application-password screen, which is where this kind of
 *   onboarding usually dies.
 *
 *   Application password (fallback) — for someone who would rather not install
 *   a plugin. It works, with a smaller set of capabilities, and the UI says so
 *   rather than letting them discover it later.
 *
 * The screen leads with the preflight checks instead of a green tick. A failed
 * connection here is almost always one of a handful of specific, fixable
 * things, and naming it ("your host is stripping the Authorization header")
 * is the difference between a five-minute fix and a support ticket.
 */

const S = {
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 20,
    background: "#fff",
    marginBottom: 16,
  },
  h: { margin: "0 0 4px", fontSize: 16, fontWeight: 600 },
  sub: { margin: "0 0 16px", fontSize: 13, color: "#6b7280", lineHeight: 1.5 },
  row: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  btn: {
    padding: "8px 14px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#fff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
  },
  btnPrimary: {
    padding: "8px 14px",
    borderRadius: 6,
    border: "1px solid #1f2937",
    background: "#1f2937",
    color: "#fff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
  },
  btnDanger: {
    padding: "8px 14px",
    borderRadius: 6,
    border: "1px solid #fca5a5",
    background: "#fff",
    color: "#b91c1c",
    cursor: "pointer",
    fontSize: 13,
  },
  code: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 22,
    letterSpacing: 2,
    padding: "12px 16px",
    background: "#f9fafb",
    border: "1px dashed #d1d5db",
    borderRadius: 8,
    display: "inline-block",
  },
  input: {
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    fontSize: 13,
    minWidth: 220,
  },
  label: { display: "block", fontSize: 12, color: "#374151", marginBottom: 4, fontWeight: 500 },
  pill: (bg, fg) => ({
    display: "inline-block",
    padding: "3px 9px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    background: bg,
    color: fg,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  }),
  checkRow: {
    display: "flex",
    gap: 10,
    padding: "10px 0",
    borderTop: "1px solid #f3f4f6",
    fontSize: 13,
    alignItems: "flex-start",
  },
  fix: {
    marginTop: 6,
    fontSize: 12,
    color: "#92400e",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 6,
    padding: "8px 10px",
    whiteSpace: "pre-wrap",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    lineHeight: 1.5,
  },
};

const STATUS_STYLE = {
  verified: ["#dcfce7", "#166534", "Connected"],
  degraded: ["#fef9c3", "#854d0e", "Connected, limited"],
  pending: ["#e0e7ff", "#3730a3", "Waiting"],
  revoked: ["#fee2e2", "#991b1b", "Access revoked"],
  error: ["#fee2e2", "#991b1b", "Not working"],
  unconfigured: ["#f3f4f6", "#4b5563", "Not connected"],
};

export default function CmsConnection({ project, onChange }) {
  const [connection, setConnection] = useState(null);
  const [plugin, setPlugin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [pairing, setPairing] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({ username: "", applicationPassword: "" });
  const pollRef = useRef(null);

  const projectId = project?.id || project?._id;

  const load = useCallback(
    async (quiet = false) => {
      try {
        const { data } = await api.getCmsConnection(projectId);
        setConnection(data.connection);
        if (data.plugin) setPlugin(data.plugin);
        if (onChange) onChange(data.connection);
        return data.connection;
      } catch (err) {
        if (!quiet) toast.error(err.response?.data?.error || "Could not load the connection");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [projectId, onChange],
  );

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);

  /**
   * While a pairing code is outstanding, the plugin completes the connection
   * from the other side — so this screen has to find out by asking. Polling
   * stops as soon as the status moves or the code expires, so it never runs
   * in the background indefinitely.
   */
  const startPolling = (expiresAt) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (new Date(expiresAt) < new Date()) {
        clearInterval(pollRef.current);
        setPairing(null);
        toast.info("That pairing code expired. Generate a new one.");
        return;
      }
      const next = await load(true);
      if (next && ["verified", "degraded", "error", "revoked"].includes(next.status)) {
        clearInterval(pollRef.current);
        setPairing(null);
        if (["verified", "degraded"].includes(next.status)) toast.success("Site connected");
      }
    }, 4000);
  };

  const handlePair = async () => {
    setBusy("pair");
    try {
      const { data } = await api.startCmsPairing(projectId);
      setPairing(data);
      startPolling(data.expiresAt);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not start pairing");
    } finally {
      setBusy(null);
    }
  };

  const handleManualSave = async (e) => {
    e.preventDefault();
    setBusy("manual");
    try {
      const { data } = await api.saveCmsCredentials(projectId, {
        provider: "wordpress",
        ...manual,
      });
      setConnection(data.connection);
      setManual({ username: "", applicationPassword: "" });
      if (data.success) toast.success("Site connected");
      else toast.warn("Saved, but the checks below did not all pass");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save those credentials");
    } finally {
      setBusy(null);
    }
  };

  const handleVerify = async () => {
    setBusy("verify");
    try {
      const { data } = await api.verifyCmsConnection(projectId);
      setConnection(data.connection);
      toast[data.success ? "success" : "warn"](
        data.verification?.diagnostics?.summary || (data.success ? "Verified" : "Checks failed"),
      );
    } catch (err) {
      toast.error(err.response?.data?.error || "Verification failed");
    } finally {
      setBusy(null);
    }
  };

  const handleWrites = async (enabled) => {
    // Turning writes ON is the moment this stops being read-only, so it is
    // confirmed explicitly rather than being a silent toggle.
    if (
      enabled &&
      !window.confirm(
        `Allow SaffronOS to change ${project?.domain} directly?\n\n` +
          "Only changes you approve one by one will ever be applied, and each one can be undone. " +
          "Nothing happens automatically.",
      )
    ) {
      return;
    }
    setBusy("writes");
    try {
      const { data } = await api.setCmsWritesEnabled(projectId, enabled);
      setConnection(data.connection);
      toast.success(enabled ? "Approved changes can now be applied" : "Writes switched off");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not change that setting");
    } finally {
      setBusy(null);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect this site? Changes already applied stay as they are.")) return;
    setBusy("disconnect");
    try {
      const { data } = await api.disconnectCms(projectId);
      setConnection(data.connection);
      toast.success("Disconnected");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not disconnect");
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div style={S.card}>Loading connection…</div>;

  const status = connection?.status || "unconfigured";
  const [bg, fg, label] = STATUS_STYLE[status] || STATUS_STYLE.unconfigured;
  const isLive = ["verified", "degraded"].includes(status);
  const checks = connection?.diagnostics?.checks || [];

  return (
    <div>
      <div style={S.card}>
        <div style={{ ...S.row, justifyContent: "space-between" }}>
          <div>
            <h3 style={S.h}>Your website</h3>
            <p style={{ ...S.sub, marginBottom: 0 }}>
              {connection?.baseUrl || `https://${project?.domain}`}
              {connection?.seoPlugin && connection.seoPlugin !== "none" && (
                <> · {connection.seoPlugin}</>
              )}
              {connection?.pluginVersion && <> · connector {connection.pluginVersion}</>}
            </p>
          </div>
          <span style={S.pill(bg, fg)}>{label}</span>
        </div>

        {connection?.lastError && (
          <p style={{ ...S.sub, color: "#b91c1c", marginTop: 12, marginBottom: 0 }}>
            {connection.lastError}
          </p>
        )}

        {isLive && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid #f3f4f6",
              ...S.row,
              justifyContent: "space-between",
            }}
          >
            <div>
              <strong style={{ fontSize: 13 }}>
                {connection.writesEnabled ? "Changes can be applied" : "Review only"}
              </strong>
              <p style={{ ...S.sub, marginBottom: 0, marginTop: 2 }}>
                {connection.writesEnabled
                  ? "Approved changes will be written to the live site."
                  : "You can review everything. Nothing can be written until you turn this on."}
              </p>
            </div>
            <button
              style={connection.writesEnabled ? S.btn : S.btnPrimary}
              disabled={busy === "writes"}
              onClick={() => handleWrites(!connection.writesEnabled)}
            >
              {connection.writesEnabled ? "Switch off" : "Allow changes"}
            </button>
          </div>
        )}

        <div style={{ ...S.row, marginTop: 16 }}>
          {!connection?.configured && (
            <button style={S.btnPrimary} disabled={busy === "pair"} onClick={handlePair}>
              {busy === "pair" ? "Generating…" : "Connect WordPress"}
            </button>
          )}
          {connection?.configured && (
            <button style={S.btn} disabled={busy === "verify"} onClick={handleVerify}>
              {busy === "verify" ? "Checking…" : "Re-check connection"}
            </button>
          )}
          {connection?.configured &&
            ["error", "revoked", "pending"].includes(connection.status) && (
              <button style={S.btnPrimary} disabled={busy === "pair"} onClick={handlePair}>
                {busy === "pair" ? "Generating…" : "Pair again"}
              </button>
            )}
          {connection?.configured && (
            <button style={S.btnDanger} disabled={busy === "disconnect"} onClick={handleDisconnect}>
              Disconnect
            </button>
          )}
        </div>
      </div>

      {/* --- connector download ----------------------------------------- */}
      {!connection?.configured && (
        <div style={S.card}>
          <h3 style={S.h}>Step 1 — install the connector</h3>
          <p style={S.sub}>
            A small WordPress plugin. It lets us read and write SEO fields on{" "}
            {project?.domain || "your site"} and nothing else — never theme files, plugins or
            settings. Install it from <strong>Plugins → Add New → Upload Plugin</strong>.
          </p>

          {plugin?.available === false ? (
            <div style={S.fix}>
              The connector is not available for download on this server yet. Ask your account
              manager for the zip.
            </div>
          ) : (
            <>
              <div style={S.row}>
                <a
                  style={{ ...S.btnPrimary, textDecoration: "none", display: "inline-block" }}
                  href={api.cmsPluginDownloadUrl()}
                >
                  Download connector{plugin?.version ? ` ${plugin.version}` : ""}
                </a>
              </div>
              {/* The owner of this project frequently is not the person with
                  wp-admin access, so the link is shown as text to forward. */}
              <p style={{ ...S.sub, marginTop: 12, marginBottom: 4 }}>
                No WordPress access yourself? Send this link to whoever does:
              </p>
              <div style={{ ...S.code, fontSize: 12, letterSpacing: 0 }}>
                {typeof window !== "undefined"
                  ? new URL(api.cmsPluginDownloadUrl(), window.location.origin).href
                  : api.cmsPluginDownloadUrl()}
              </div>
            </>
          )}
        </div>
      )}

      {/* --- pairing code ----------------------------------------------- */}
      {pairing && (
        <div style={{ ...S.card, borderColor: "#c7d2fe", background: "#f5f7ff" }}>
          <h3 style={S.h}>Finish in WordPress</h3>
          <p style={S.sub}>
            This screen updates by itself once the plugin connects. The code works for 15 minutes.
          </p>
          <div style={S.code}>{pairing.code}</div>
          <ol style={{ fontSize: 13, color: "#374151", lineHeight: 1.9, marginTop: 16, paddingLeft: 20 }}>
            {pairing.instructions?.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ol>
        </div>
      )}

      {/* --- preflight results ------------------------------------------ */}
      {checks.length > 0 && (
        <div style={S.card}>
          <h3 style={S.h}>Connection checks</h3>
          <p style={S.sub}>
            {connection?.diagnostics?.summary ||
              "Each of these has to pass before anything can be applied."}
          </p>
          {checks.map((c) => (
            <div key={c.id} style={S.checkRow}>
              <span style={{ color: c.ok ? "#16a34a" : "#dc2626", fontWeight: 700, width: 14 }}>
                {c.ok ? "✓" : "✕"}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{c.label}</div>
                {c.detail && (
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{c.detail}</div>
                )}
                {!c.ok && c.fix && <div style={S.fix}>{c.fix}</div>}
                {/* A passing check can still carry advice — a page builder is
                    not a failure, but it does change what gets automated. */}
                {c.ok && c.fix && (
                  <div style={{ ...S.fix, color: "#1e40af", background: "#eff6ff", borderColor: "#bfdbfe", fontFamily: "inherit" }}>
                    {c.fix}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- manual fallback -------------------------------------------- */}
      {!connection?.configured && (
        <div style={S.card}>
          <button
            style={{ ...S.btn, border: "none", padding: 0, background: "none", color: "#2563eb" }}
            onClick={() => setShowManual((v) => !v)}
          >
            {showManual ? "Hide" : "Connect without installing the plugin"}
          </button>
          {showManual && (
            <form onSubmit={handleManualSave} style={{ marginTop: 14 }}>
              <p style={S.sub}>
                In WordPress go to <strong>Users → Profile → Application Passwords</strong>, add one
                named “SaffronOS”, and paste it here. Without the plugin we cannot read or write
                meta titles and descriptions, so those will be prepared for you to apply by hand
                instead of applied automatically.
              </p>
              <div style={{ ...S.row, alignItems: "flex-end" }}>
                <div>
                  <label style={S.label} htmlFor="wp-user">
                    WordPress username
                  </label>
                  <input
                    id="wp-user"
                    style={S.input}
                    value={manual.username}
                    onChange={(e) => setManual((m) => ({ ...m, username: e.target.value }))}
                    autoComplete="off"
                    required
                  />
                </div>
                <div>
                  <label style={S.label} htmlFor="wp-pass">
                    Application password
                  </label>
                  <input
                    id="wp-pass"
                    style={S.input}
                    type="password"
                    value={manual.applicationPassword}
                    onChange={(e) =>
                      setManual((m) => ({ ...m, applicationPassword: e.target.value }))
                    }
                    autoComplete="off"
                    required
                  />
                </div>
                <button style={S.btnPrimary} type="submit" disabled={busy === "manual"}>
                  {busy === "manual" ? "Checking…" : "Connect"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
